import React, { useState, useRef, useMemo } from "react";
import ReactDOM from "react-dom";
import { Inbox, RefreshCw, Search, X, LayoutList, LayoutGrid, ArrowUpDown } from "lucide-react";
import TaskRow from "@/components/TaskRow";
import { dueDateStatus } from "@/components/TaskDetailModal";
import { timeAgo } from "@/utils/timeAgo";

const PRIORITY_MAP = {
  urgent: { label: "Urgent", color: "#dc2626", bg: "rgba(220,38,38,0.10)", border: "rgba(220,38,38,0.25)" },
  high:   { label: "High",   color: "#d97706", bg: "rgba(217,119,6,0.10)", border: "rgba(217,119,6,0.25)" },
  low:    { label: "Low",    color: "#3b82f6", bg: "rgba(59,130,246,0.10)", border: "rgba(59,130,246,0.25)" },
};

const PRIORITY_ORDER = { urgent: 4, high: 3, medium: 2, normal: 2, low: 1 };

const CHIPS = [
  { id: "overdue",  label: "Overdue",       color: "#dc2626", activeBg: "rgba(220,38,38,0.12)",  activeBorder: "rgba(220,38,38,0.4)"  },
  { id: "priority", label: "High Priority", color: "#d97706", activeBg: "rgba(217,119,6,0.12)",  activeBorder: "rgba(217,119,6,0.4)"  },
  { id: "week",     label: "This Week",     color: "#3d6b1f", activeBg: "rgba(61,107,31,0.12)",  activeBorder: "rgba(61,107,31,0.4)"  },
];

const SORT_OPTIONS = [
  { value: "newest",   label: "Newest first" },
  { value: "oldest",   label: "Oldest first" },
  { value: "due_date", label: "Due date"      },
  { value: "priority", label: "Priority"      },
];

function applyFiltersAndSort(tasks, searchQuery, activeFilter, activeProject, sortBy) {
  const q = searchQuery.toLowerCase();
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  let result = tasks.filter((t) => {
    const matchSearch = !q || t.title.toLowerCase().includes(q) || (t.project_title || "").toLowerCase().includes(q);
    const due = t.due_date ? new Date(t.due_date) : null;
    if (due) due.setHours(0, 0, 0, 0);
    const matchFilter =
      !activeFilter ||
      (activeFilter === "overdue"  && due && due < today) ||
      (activeFilter === "priority" && (t.priority === "high" || t.priority === "urgent")) ||
      (activeFilter === "week"     && due && due >= today && due <= new Date(today.getTime() + 7 * 86400000));
    const matchProject =
      !activeProject ||
      (activeProject === "__none__" && !t.project_id) ||
      t.project_id === activeProject;
    return matchSearch && matchFilter && matchProject;
  });

  switch (sortBy) {
    case "oldest":
      result = [...result].sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
      break;
    case "due_date":
      result = [...result].sort((a, b) => {
        if (!a.due_date && !b.due_date) return 0;
        if (!a.due_date) return 1;
        if (!b.due_date) return -1;
        return new Date(a.due_date) - new Date(b.due_date);
      });
      break;
    case "priority":
      result = [...result].sort((a, b) =>
        (PRIORITY_ORDER[b.priority] || 2) - (PRIORITY_ORDER[a.priority] || 2)
      );
      break;
    default:
      result = [...result].sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
  }
  return result;
}

/* ── Assign dropdown (portal) ──────────────────────────────────────────────── */
function AssignPortal({ pos, projects, taskId, onAssign, onClose }) {
  return ReactDOM.createPortal(
    <>
      <div style={{ position: "fixed", inset: 0, zIndex: 999 }} onClick={onClose} />
      <div style={{
        position: "fixed", top: pos.top, left: pos.left, zIndex: 1000,
        background: "rgba(255,255,255,0.98)", backdropFilter: "blur(12px)",
        WebkitBackdropFilter: "blur(12px)",
        borderRadius: "0.5rem", border: "1px solid rgba(26,77,46,0.18)",
        boxShadow: "0 8px 28px rgba(26,77,46,0.16)", minWidth: "180px", overflow: "hidden",
      }}>
        {[{ id: null, emoji: "—", title: "No project" }, ...projects].map((p) => (
          <button
            key={p.id ?? "none"}
            onClick={() => { onAssign(taskId, p.id); onClose(); }}
            style={{
              display: "block", width: "100%", textAlign: "left",
              padding: "0.4rem 0.75rem", background: "transparent", border: "none",
              cursor: "pointer", fontFamily: "'Manrope', sans-serif",
              fontSize: "0.8rem", color: "#1A4D2E", transition: "background 0.12s",
            }}
            onMouseEnter={(e) => (e.currentTarget.style.background = "rgba(26,77,46,0.06)")}
            onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
          >
            {p.emoji} {p.title}
          </button>
        ))}
      </div>
    </>,
    document.body
  );
}

/* ── Grid Card ──────────────────────────────────────────────────────────────── */
function TaskCard({ task, fading, isNew, projects, onComplete, onDelete, onAssign, onOpenDetail }) {
  const [checked, setChecked] = useState(false);
  const [cardHover, setCardHover] = useState(false);
  const [showAssign, setShowAssign] = useState(false);
  const [assignPos, setAssignPos] = useState({ top: 0, left: 0 });
  const assignRef = useRef(null);

  const due = dueDateStatus(task.due_date);
  const priBadge = PRIORITY_MAP[task.priority];

  const handleAssignClick = () => {
    if (!showAssign && assignRef.current) {
      const r = assignRef.current.getBoundingClientRect();
      setAssignPos({ top: r.bottom + 4, left: r.left });
    }
    setShowAssign((v) => !v);
  };

  return (
    <div style={{
      opacity: fading ? 0 : 1,
      transform: fading ? "scale(0.96)" : "scale(1)",
      transition: "opacity 0.35s, transform 0.35s",
      animation: isNew ? "taskEnter 0.38s ease-out" : "none",
    }}>
      <div
        data-testid={`task-row-${task.id}`}
        onMouseEnter={() => setCardHover(true)}
        onMouseLeave={() => setCardHover(false)}
        style={{
          background: cardHover ? "#ffffff" : "rgba(255,255,255,0.94)",
          backdropFilter: "blur(14px)", WebkitBackdropFilter: "blur(14px)",
          border: "1px solid rgba(255,255,255,0.7)",
          borderRadius: "0.875rem", padding: "1.1rem",
          display: "flex", flexDirection: "column", gap: "0.6rem",
          boxShadow: cardHover ? "0 6px 22px rgba(26,77,46,0.13)" : "0 2px 8px rgba(26,77,46,0.06)",
          transition: "box-shadow 0.18s, background 0.18s",
          minHeight: "140px",
        }}
      >
        {/* Row 1: checkbox + project tag + delete */}
        <div style={{ display: "flex", alignItems: "flex-start", gap: "0.5rem" }}>
          <div
            data-testid={`task-checkbox-${task.id}`}
            onClick={() => { setChecked(true); setTimeout(() => onComplete(task.id), 120); }}
            style={{
              width: "18px", height: "18px", borderRadius: "50%", flexShrink: 0, marginTop: "1px",
              border: `1.5px solid ${checked ? "#3d6b1f" : "rgba(26,77,46,0.35)"}`,
              background: checked ? "#3d6b1f" : "transparent",
              cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center",
              transition: "background 0.15s, border-color 0.15s",
            }}
          >
            {checked && (
              <svg width="9" height="7" viewBox="0 0 9 7" fill="none">
                <path d="M1 3.5L3.5 6L8 1" stroke="#fff" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            )}
          </div>

          <div style={{ flex: 1, minWidth: 0 }}>
            {task.project_title && (
              <span style={{
                fontFamily: "'Manrope', sans-serif", fontSize: "0.67rem", fontWeight: 600,
                background: "rgba(61,107,31,0.1)", color: "#3d6b1f",
                border: "1px solid rgba(61,107,31,0.2)", borderRadius: "0.3rem",
                padding: "0.05rem 0.4rem",
              }}>
                {task.project_emoji} {task.project_title}
              </span>
            )}
          </div>

          <button
            data-testid={`task-delete-${task.id}`}
            onClick={() => onDelete(task.id)}
            style={{
              background: "none", border: "none", cursor: "pointer",
              color: "#cbd5e1", padding: "2px", display: "flex", flexShrink: 0,
              transition: "color 0.15s",
            }}
            onMouseEnter={(e) => (e.currentTarget.style.color = "#ef4444")}
            onMouseLeave={(e) => (e.currentTarget.style.color = "#cbd5e1")}
          >
            <X size={14} />
          </button>
        </div>

        {/* Task title */}
        <p
          data-testid={`task-title-${task.id}`}
          onClick={() => onOpenDetail?.(task)}
          style={{
            fontFamily: "'Manrope', sans-serif", color: "#1A4D2E",
            fontSize: "0.92rem", fontWeight: 600, margin: 0, lineHeight: 1.45,
            textDecoration: checked ? "line-through" : "none",
            opacity: checked ? 0.45 : 1,
            display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical",
            overflow: "hidden", cursor: "pointer", transition: "opacity 0.2s",
            flex: 1,
          }}
          onMouseEnter={(e) => { if (!checked) e.currentTarget.style.color = "#3d6b1f"; }}
          onMouseLeave={(e) => { e.currentTarget.style.color = "#1A4D2E"; }}
        >
          {task.title}
        </p>

        {/* Priority + due date badges */}
        {(priBadge || due) && (
          <div style={{ display: "flex", flexWrap: "wrap", gap: "0.25rem" }}>
            {priBadge && (
              <span style={{
                fontFamily: "'Manrope', sans-serif", fontSize: "0.64rem", fontWeight: 700,
                background: priBadge.bg, color: priBadge.color,
                border: `1px solid ${priBadge.border}`, borderRadius: "0.3rem",
                padding: "0.05rem 0.4rem", textTransform: "capitalize",
              }}>
                {priBadge.label}
              </span>
            )}
            {due && (
              <span style={{
                fontFamily: "'Manrope', sans-serif", fontSize: "0.64rem", fontWeight: 600,
                background: due.bg, color: due.color,
                border: `1px solid ${due.border}`, borderRadius: "0.3rem",
                padding: "0.05rem 0.4rem",
              }}>
                {due.label}
              </span>
            )}
          </div>
        )}

        {/* Footer: created time + assign */}
        <div style={{
          display: "flex", alignItems: "center", justifyContent: "space-between",
          paddingTop: "0.5rem", borderTop: "1px solid rgba(26,77,46,0.07)", marginTop: "auto",
        }}>
          <span style={{ fontFamily: "'Manrope', sans-serif", color: "#9ca3af", fontSize: "0.7rem" }}>
            {timeAgo(task.created_at)}
          </span>
          {projects && projects.length > 0 && (
            <button
              ref={assignRef}
              data-testid={`assign-btn-${task.id}`}
              onClick={handleAssignClick}
              style={{
                background: "none", border: "none", cursor: "pointer",
                fontFamily: "'Manrope', sans-serif", fontSize: "0.68rem",
                color: "#9ca3af", padding: "2px 5px", borderRadius: "0.25rem",
                transition: "color 0.15s, background 0.15s",
              }}
              onMouseEnter={(e) => { e.currentTarget.style.color = "#3d6b1f"; e.currentTarget.style.background = "rgba(61,107,31,0.08)"; }}
              onMouseLeave={(e) => { e.currentTarget.style.color = "#9ca3af"; e.currentTarget.style.background = "none"; }}
            >
              {task.project_title ? "Move ▾" : "Assign ▾"}
            </button>
          )}
        </div>
      </div>

      {showAssign && (
        <AssignPortal
          pos={assignPos}
          projects={projects}
          taskId={task.id}
          onAssign={onAssign}
          onClose={() => setShowAssign(false)}
        />
      )}
    </div>
  );
}

/* ── Main InboxSection ──────────────────────────────────────────────────────── */
export default function InboxSection({
  tasks, projects, fadingIds, newIds,
  onRefresh, onComplete, onDelete, onAssign, onOpenDetail,
}) {
  const [searchQuery, setSearchQuery]   = useState("");
  const [activeFilter, setActiveFilter] = useState(null);
  const [activeProject, setActiveProject] = useState(null);
  const [sortBy, setSortBy]             = useState("newest");
  const [viewMode, setViewMode]         = useState("list");
  const [refreshing, setRefreshing]     = useState(false);

  // Build project chips from tasks actually in the inbox
  const projectsInInbox = useMemo(() => {
    const map = {};
    tasks.forEach((t) => {
      if (t.project_id) {
        map[t.project_id] = { id: t.project_id, title: t.project_title || "Untitled", emoji: t.project_emoji || "" };
      }
    });
    return Object.values(map).sort((a, b) => a.title.localeCompare(b.title));
  }, [tasks]);

  const hasUnassigned = tasks.some((t) => !t.project_id);

  const filtered = applyFiltersAndSort(tasks, searchQuery, activeFilter, activeProject, sortBy);
  const hasFilter = searchQuery.trim() || activeFilter || activeProject;

  const handleRefresh = async () => {
    setRefreshing(true);
    await onRefresh();
    setTimeout(() => setRefreshing(false), 400);
  };

  const TOOLBAR = {
    background: "rgba(255,255,255,0.72)",
    backdropFilter: "blur(14px)",
    WebkitBackdropFilter: "blur(14px)",
    border: "1px solid rgba(255,255,255,0.7)",
    borderRadius: "0.875rem",
    boxShadow: "0 4px 24px rgba(26,77,46,0.07)",
    width: "100%",
  };

  return (
    <>
      <style>{`
        @keyframes taskEnter { from { opacity: 0; transform: translateY(6px); } to { opacity: 1; transform: none; } }
        .inbox-card-scroll::-webkit-scrollbar { width: 0; }
        .inbox-header-controls { margin-left: auto; display: flex; align-items: center; gap: 0.5rem; }
        @media (max-width: 520px) {
          .inbox-header-controls { margin-left: 0; width: 100%; justify-content: flex-end; }
        }
      `}</style>

      {/* ── Header card ─────────────────────────────────────────────────────── */}
      <div style={TOOLBAR}>
        <div style={{
          padding: "0.875rem 1.25rem",
          display: "flex", alignItems: "center", gap: "0.75rem", flexWrap: "wrap",
        }}>
          {/* Title + count */}
          <Inbox size={18} color="#3d6b1f" />
          <span
            data-testid="inbox-heading"
            style={{ fontFamily: "'Manrope', sans-serif", color: "#1A4D2E", fontSize: "1.05rem", fontWeight: 800 }}
          >
            Inbox
          </span>
          <span
            data-testid="task-count"
            style={{
              fontFamily: "'Manrope', sans-serif", fontSize: "0.75rem", fontWeight: 700,
              background: "rgba(26,77,46,0.1)", color: "#1A4D2E",
              borderRadius: "2rem", padding: "0.15rem 0.6rem",
            }}
          >
            {hasFilter ? `${filtered.length} / ${tasks.length}` : tasks.length} tasks
          </span>

          <div className="inbox-header-controls">
            {/* Sort dropdown */}
            <div style={{ position: "relative", display: "flex", alignItems: "center" }}>
              <ArrowUpDown size={13} color="#6b7280" style={{ position: "absolute", left: "0.6rem", pointerEvents: "none" }} />
              <select
                data-testid="sort-select"
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                style={{
                  fontFamily: "'Manrope', sans-serif", fontSize: "0.78rem", color: "#374151",
                  background: "rgba(255,255,255,0.7)", border: "1px solid rgba(26,77,46,0.18)",
                  borderRadius: "0.5rem", padding: "0.35rem 0.75rem 0.35rem 1.9rem",
                  cursor: "pointer", outline: "none", appearance: "none",
                }}
              >
                {SORT_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            </div>

            {/* View toggle */}
            <div style={{
              display: "flex", background: "rgba(26,77,46,0.07)",
              borderRadius: "0.5rem", padding: "2px", gap: "2px",
            }}>
              {[
                { mode: "list", Icon: LayoutList  },
                { mode: "grid", Icon: LayoutGrid  },
              ].map(({ mode, Icon }) => (
                <button
                  key={mode}
                  data-testid={`view-${mode}`}
                  onClick={() => setViewMode(mode)}
                  title={`${mode.charAt(0).toUpperCase() + mode.slice(1)} view`}
                  style={{
                    display: "flex", alignItems: "center", justifyContent: "center",
                    padding: "0.3rem 0.45rem", border: "none", cursor: "pointer",
                    borderRadius: "0.375rem",
                    background: viewMode === mode ? "#fff" : "transparent",
                    color: viewMode === mode ? "#1A4D2E" : "#9ca3af",
                    boxShadow: viewMode === mode ? "0 1px 4px rgba(26,77,46,0.12)" : "none",
                    transition: "background 0.15s, color 0.15s",
                  }}
                >
                  <Icon size={15} />
                </button>
              ))}
            </div>

            {/* Refresh */}
            <button
              data-testid="inbox-refresh-btn"
              onClick={handleRefresh}
              title="Refresh"
              style={{
                background: "none", border: "none", cursor: "pointer",
                color: "#6b7280", display: "flex", padding: "0.3rem",
                borderRadius: "0.375rem", transition: "color 0.15s, background 0.15s",
              }}
              onMouseEnter={(e) => { e.currentTarget.style.color = "#3d6b1f"; e.currentTarget.style.background = "rgba(61,107,31,0.08)"; }}
              onMouseLeave={(e) => { e.currentTarget.style.color = "#6b7280"; e.currentTarget.style.background = "none"; }}
            >
              <RefreshCw
                size={15}
                style={{ transition: "transform 0.4s ease", transform: refreshing ? "rotate(360deg)" : "none" }}
              />
            </button>
          </div>
        </div>

        {/* Filter chips + search */}
        {tasks.length > 0 && (
          <div style={{ padding: "0 1.25rem 0.875rem", display: "flex", flexDirection: "column", gap: "0.5rem" }}>
            {/* Status chips row */}
            <div style={{ display: "flex", gap: "0.4rem", flexWrap: "wrap" }}>
              {CHIPS.map(({ id, label, color, activeBg, activeBorder }) => {
                const active = activeFilter === id;
                return (
                  <button
                    key={id}
                    data-testid={`filter-chip-${id}`}
                    onClick={() => setActiveFilter(active ? null : id)}
                    style={{
                      padding: "0.25rem 0.75rem", borderRadius: "2rem",
                      border: `1.5px solid ${active ? activeBorder : "rgba(26,77,46,0.18)"}`,
                      background: active ? activeBg : "rgba(255,255,255,0.45)",
                      color: active ? color : "#6b7280",
                      fontFamily: "'Manrope', sans-serif", fontSize: "0.75rem",
                      fontWeight: active ? 700 : 500, cursor: "pointer",
                      transition: "all 0.15s", whiteSpace: "nowrap",
                    }}
                    onMouseEnter={(e) => { if (!active) { e.currentTarget.style.borderColor = activeBorder; e.currentTarget.style.color = color; } }}
                    onMouseLeave={(e) => { if (!active) { e.currentTarget.style.borderColor = "rgba(26,77,46,0.18)"; e.currentTarget.style.color = "#6b7280"; } }}
                  >
                    {label}
                  </button>
                );
              })}
            </div>

            {/* Project filter chips row */}
            {(projectsInInbox.length > 0 || hasUnassigned) && (
              <div style={{ display: "flex", gap: "0.4rem", flexWrap: "wrap", alignItems: "center" }}>
                <span style={{
                  fontFamily: "'Manrope', sans-serif", fontSize: "0.7rem",
                  fontWeight: 600, color: "#9ca3af", letterSpacing: "0.05em",
                  textTransform: "uppercase", whiteSpace: "nowrap",
                  paddingRight: "0.15rem",
                }}>
                  Project
                </span>
                {hasUnassigned && (
                  <button
                    data-testid="filter-project-none"
                    onClick={() => setActiveProject(activeProject === "__none__" ? null : "__none__")}
                    style={{
                      padding: "0.2rem 0.65rem", borderRadius: "2rem",
                      border: `1.5px solid ${activeProject === "__none__" ? "rgba(107,114,128,0.5)" : "rgba(26,77,46,0.15)"}`,
                      background: activeProject === "__none__" ? "rgba(107,114,128,0.12)" : "rgba(255,255,255,0.45)",
                      color: activeProject === "__none__" ? "#374151" : "#9ca3af",
                      fontFamily: "'Manrope', sans-serif", fontSize: "0.73rem",
                      fontWeight: activeProject === "__none__" ? 700 : 500,
                      cursor: "pointer", transition: "all 0.15s", whiteSpace: "nowrap",
                    }}
                  >
                    Unassigned
                  </button>
                )}
                {projectsInInbox.map(({ id, title, emoji }) => {
                  const active = activeProject === id;
                  return (
                    <button
                      key={id}
                      data-testid={`filter-project-${id}`}
                      onClick={() => setActiveProject(active ? null : id)}
                      style={{
                        padding: "0.2rem 0.65rem", borderRadius: "2rem",
                        border: `1.5px solid ${active ? "rgba(26,77,46,0.45)" : "rgba(26,77,46,0.15)"}`,
                        background: active ? "rgba(26,77,46,0.1)" : "rgba(255,255,255,0.45)",
                        color: active ? "#1A4D2E" : "#6b7280",
                        fontFamily: "'Manrope', sans-serif", fontSize: "0.73rem",
                        fontWeight: active ? 700 : 500,
                        cursor: "pointer", transition: "all 0.15s", whiteSpace: "nowrap",
                        display: "flex", alignItems: "center", gap: "0.3rem",
                      }}
                    >
                      {emoji && <span style={{ fontSize: "0.8rem" }}>{emoji}</span>}
                      {title}
                    </button>
                  );
                })}
              </div>
            )}

            <div style={{ position: "relative", display: "flex", alignItems: "center" }}>
              <Search size={14} color="#9ca3af" style={{ position: "absolute", left: "0.75rem" }} />
              <input
                data-testid="inbox-search-input"
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search tasks or projects…"
                style={{
                  width: "100%",
                  background: "rgba(255,255,255,0.55)",
                  border: "1px solid rgba(26,77,46,0.15)",
                  borderRadius: "0.5rem",
                  padding: "0.45rem 2.25rem 0.45rem 2.25rem",
                  color: "#0F172A", fontSize: "0.85rem",
                  fontFamily: "'Manrope', sans-serif",
                  outline: "none", transition: "border-color 0.2s",
                }}
                onFocus={(e) => (e.target.style.borderColor = "#3d6b1f")}
                onBlur={(e) => (e.target.style.borderColor = "rgba(26,77,46,0.15)")}
              />
              {searchQuery && (
                <button
                  data-testid="inbox-search-clear-btn"
                  onClick={() => setSearchQuery("")}
                  style={{
                    position: "absolute", right: "0.75rem",
                    background: "none", border: "none", cursor: "pointer",
                    color: "#9ca3af", display: "flex", padding: "2px",
                  }}
                  onMouseEnter={(e) => (e.currentTarget.style.color = "#374151")}
                  onMouseLeave={(e) => (e.currentTarget.style.color = "#9ca3af")}
                >
                  <X size={13} />
                </button>
              )}
            </div>
          </div>
        )}
      </div>

      {/* ── Content ─────────────────────────────────────────────────────────── */}
      {tasks.length === 0 ? (
        <div
          data-testid="empty-state"
          style={{
            width: "100%", padding: "4rem 1rem",
            display: "flex", flexDirection: "column", alignItems: "center",
            gap: "0.5rem", textAlign: "center",
            background: "rgba(255,255,255,0.55)", backdropFilter: "blur(12px)",
            WebkitBackdropFilter: "blur(12px)",
            border: "1px solid rgba(255,255,255,0.6)", borderRadius: "0.875rem",
          }}
        >
          <span style={{ fontSize: "2rem" }}>🎉</span>
          <p style={{ fontFamily: "'Manrope', sans-serif", color: "#1A4D2E", fontSize: "1rem", fontWeight: 700, margin: 0 }}>
            Your inbox is empty
          </p>
          <p style={{ fontFamily: "'Manrope', sans-serif", color: "#6b7280", fontSize: "0.85rem", margin: 0 }}>
            Add tasks using the bar above
          </p>
        </div>
      ) : filtered.length === 0 ? (
        <div
          data-testid="search-no-results"
          style={{
            width: "100%", padding: "3rem 1rem", textAlign: "center",
            background: "rgba(255,255,255,0.55)", backdropFilter: "blur(12px)",
            WebkitBackdropFilter: "blur(12px)",
            border: "1px solid rgba(255,255,255,0.6)", borderRadius: "0.875rem",
          }}
        >
          <p style={{ fontFamily: "'Manrope', sans-serif", color: "#6b7280", fontSize: "0.9rem", margin: 0 }}>
            No tasks match &ldquo;{searchQuery || activeFilter}&rdquo;
          </p>
        </div>
      ) : viewMode === "grid" ? (
        /* ── Grid view ── */
        <div
          data-testid="inbox-task-list"
          style={{
            width: "100%",
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))",
            gap: "0.875rem",
          }}
        >
          {filtered.map((task) => (
            <TaskCard
              key={task.id}
              task={task}
              fading={fadingIds.has(task.id)}
              isNew={newIds.has(task.id)}
              projects={projects}
              onComplete={onComplete}
              onDelete={onDelete}
              onAssign={onAssign}
              onOpenDetail={onOpenDetail}
            />
          ))}
        </div>
      ) : (
        /* ── List view ── */
        <div
          data-testid="inbox-task-list"
          style={{
            width: "100%",
            background: "rgba(255,255,255,0.72)",
            backdropFilter: "blur(14px)", WebkitBackdropFilter: "blur(14px)",
            border: "1px solid rgba(255,255,255,0.7)",
            borderRadius: "0.875rem",
            boxShadow: "0 4px 24px rgba(26,77,46,0.07)",
            padding: "0.625rem 0.875rem",
            display: "flex", flexDirection: "column",
          }}
        >
          {filtered.map((task) => (
            <TaskRow
              key={task.id}
              task={task}
              fading={fadingIds.has(task.id)}
              isNew={newIds.has(task.id)}
              projects={projects}
              onComplete={onComplete}
              onDelete={onDelete}
              onAssign={onAssign}
              onOpenDetail={onOpenDetail}
            />
          ))}
        </div>
      )}
    </>
  );
}
